/**
 * Beyza Fashion — global product card behaviour.
 *
 * One delegated set of listeners handles every card on the page (including cards
 * added later by infinite scroll, filters or recommendations), plus ONE shared
 * Quick View <dialog>. Cart additions use Shopify's AJAX Cart API and the theme's
 * own CartAddEvent, so the existing cart drawer, cart count and auto-open work unchanged.
 */

const CONFIG_EL = document.getElementById('BeyzaProductCardConfig');
const DIALOG = document.getElementById('BeyzaQuickView');

if (CONFIG_EL && !window.__beyzaProductCard) {
  window.__beyzaProductCard = true;
  init();
}

function init() {
  /** @type {any} */
  let config = {};
  try {
    config = JSON.parse(CONFIG_EL.textContent || '{}');
  } catch (e) {
    config = {};
  }
  const S = Object.assign(
    {
      add: 'Add to cart',
      adding: 'Adding...',
      added: 'Added to cart',
      error: 'Error adding to cart',
      soldOut: 'Sold out',
      unavailable: 'Unavailable',
      quantity: 'Quantity',
      close: 'Close',
      salePrice: 'Sale price',
      regularPrice: 'Regular price',
      viewFull: 'View full product',
      loading: 'Loading product',
      decrease: 'Decrease quantity',
      increase: 'Increase quantity',
      wishlistAdd: 'Add to wishlist',
      wishlistRemove: 'Remove from wishlist',
      wishlistAdded: 'Added to wishlist',
      wishlistRemoved: 'Removed from wishlist',
      image: 'Image',
    },
    config.strings || {}
  );

  const statusEl = document.getElementById('BeyzaCardStatus');
  const announce = (msg) => {
    if (!statusEl) return;
    statusEl.textContent = '';
    window.setTimeout(() => { statusEl.textContent = msg; }, 50);
  };

  /* ---------------- Theme modules (optional, never fatal) ---------------- */
  /** @type {Promise<any>} */
  const eventsModule = import('@theme/events').catch(() => null);
  /** @type {any} */
  let moneyModule = null;
  import('@theme/money-formatting').then((m) => { moneyModule = m; }).catch(() => {});

  function formatMoney(cents) {
    const format = (config.useCurrencyCode ? config.moneyWithCurrencyFormat : config.moneyFormat) || '{{amount}}';
    const currency = config.currency || 'INR';
    if (moneyModule && typeof moneyModule.formatMoney === 'function') {
      try { return moneyModule.formatMoney(cents, format, currency); } catch (e) { /* fall through */ }
    }
    try {
      return new Intl.NumberFormat(config.locale || undefined, { style: 'currency', currency }).format(cents / 100);
    } catch (e) {
      return String((cents / 100).toFixed(2));
    }
  }

  /* ---------------- Cart ---------------- */
  const cartAddUrl = config.cartAddUrl || '/cart/add.js';
  const cartUrl = config.cartUrl || '/cart.js';

  /**
   * Adds a variant to the cart using the theme's contract.
   * @param {number|string} variantId
   * @param {number} quantity
   * @param {HTMLElement} sourceEl element the theme event is dispatched from
   * @param {{ productId?: string, beforeDispatch?: () => void }} [opts]
   */
  async function addToCart(variantId, quantity, sourceEl, opts = {}) {
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el instanceof HTMLElement ? el.dataset.sectionId : '')
      .filter(Boolean);

    const body = new FormData();
    body.append('id', String(variantId));
    body.append('quantity', String(quantity));
    if (sectionIds.length) {
      body.append('sections', sectionIds.join(','));
      body.append('sections_url', window.location.pathname);
    }

    const res = await fetch(cartAddUrl, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body,
    });
    let data = {};
    try { data = await res.json(); } catch (e) { data = {}; }
    if (!res.ok || data.status) {
      const err = new Error(data.description || data.message || S.error);
      err.name = 'CartError';
      throw err;
    }

    let cart = null;
    try {
      const r = await fetch(cartUrl, { headers: { Accept: 'application/json' } });
      if (r.ok) cart = await r.json();
    } catch (e) { cart = null; }

    if (typeof opts.beforeDispatch === 'function') opts.beforeDispatch();

    const detailData = {
      source: cart ? 'beyza-product-card' : 'product-form-component',
      itemCount: cart ? cart.item_count : quantity,
      productId: opts.productId || '',
      variantId: String(variantId),
      sections: data.sections || undefined,
    };
    const target = sourceEl && sourceEl.isConnected ? sourceEl : document;
    const events = await eventsModule;
    if (events && events.CartAddEvent) {
      target.dispatchEvent(new events.CartAddEvent(cart || {}, String(variantId), detailData));
    } else {
      target.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: { resource: cart || {}, sourceId: String(variantId), data: detailData },
      }));
    }
    document.dispatchEvent(new CustomEvent('beyza:cart:added', { detail: { variantId, quantity, cart, item: data } }));
    return { item: data, cart };
  }

  /* ---------------- Card helpers ---------------- */
  const cardOf = (el) => el.closest('[data-beyza-card]');

  function setButtonLabel(btn, text) {
    const label = btn.querySelector('[data-beyza-add-label]') || btn;
    label.textContent = text;
  }

  function setCardAvailability(card, available) {
    card.querySelectorAll('[data-beyza-add]').forEach((btn) => {
      btn.disabled = !available;
      btn.classList.remove('is-added');
      setButtonLabel(btn, available ? S.add : S.soldOut);
    });
  }

  function buildPrice(container, priceText, compareText, pct, cls) {
    container.textContent = '';
    const prefix = cls || 'beyza-product-card';
    const onSale = !!compareText;
    if (onSale) {
      const vh = document.createElement('span');
      vh.className = 'beyza-product-card__vh';
      vh.textContent = S.salePrice;
      container.appendChild(vh);
    }
    const cur = document.createElement('span');
    cur.className = prefix === 'beyza-product-card' ? 'beyza-product-card__price-current' : '';
    if (onSale) cur.classList.add('is-sale');
    cur.textContent = priceText;
    container.appendChild(cur);
    if (onSale && config.showCompare !== false) {
      const vh2 = document.createElement('span');
      vh2.className = 'beyza-product-card__vh';
      vh2.textContent = S.regularPrice;
      const s = document.createElement('s');
      s.className = prefix === 'beyza-product-card' ? 'beyza-product-card__price-compare' : '';
      s.textContent = compareText;
      container.append(vh2, s);
    }
    if (onSale && config.showSalePct !== false && pct > 0) {
      const save = document.createElement('span');
      save.className = prefix === 'beyza-product-card' ? 'beyza-product-card__price-save' : 'beyza-quick-view__save';
      save.textContent = `-${pct}%`;
      container.appendChild(save);
    }
  }

  function selectSwatch(card, swatch) {
    card.querySelectorAll('[data-beyza-swatch]').forEach((s) => s.setAttribute('aria-pressed', s === swatch ? 'true' : 'false'));
    const variantId = swatch.dataset.variantId;
    if (variantId) card.dataset.variantId = variantId;

    const url = swatch.dataset.url;
    if (url) card.querySelectorAll('[data-beyza-link]').forEach((a) => a.setAttribute('href', url));

    const img = card.querySelector('.beyza-product-card__image--primary');
    if (img && swatch.dataset.image) {
      img.setAttribute('srcset', swatch.dataset.srcset || '');
      img.setAttribute('src', swatch.dataset.image);
      const track = card.querySelector('[data-beyza-track]');
      if (track && track.scrollLeft) track.scrollTo({ left: 0, behavior: 'auto' });
    }

    const priceEl = card.querySelector('[data-beyza-price]');
    if (priceEl && swatch.dataset.price) {
      buildPrice(priceEl, swatch.dataset.price, swatch.dataset.compare || '', Number(swatch.dataset.pct || 0));
    }
    setCardAvailability(card, swatch.dataset.available === 'true');
  }

  /* Button state machine: default → loading → success | error → default */
  async function runAdd(btn, task) {
    if (btn.disabled || btn.getAttribute('aria-busy') === 'true') return;
    const idle = btn.querySelector('[data-beyza-add-label]') ? btn.querySelector('[data-beyza-add-label]').textContent : btn.textContent;
    btn.setAttribute('aria-busy', 'true');
    btn.disabled = true;
    setButtonLabel(btn, S.adding);
    try {
      await task();
      btn.classList.add('is-added');
      setButtonLabel(btn, S.added);
      announce(S.added);
      window.setTimeout(() => {
        btn.classList.remove('is-added');
        btn.removeAttribute('aria-busy');
        btn.disabled = false;
        setButtonLabel(btn, idle || S.add);
      }, 2000);
    } catch (e) {
      const message = e && e.message ? e.message : S.error;
      setButtonLabel(btn, S.error);
      announce(message);
      window.setTimeout(() => {
        btn.removeAttribute('aria-busy');
        btn.disabled = false;
        setButtonLabel(btn, idle || S.add);
      }, 2500);
      throw e;
    }
  }

  /* ---------------- Wishlist (local, app-ready) ---------------- */
  const WL_KEY = 'beyza:wishlist';
  const readWishlist = () => {
    try { return JSON.parse(window.localStorage.getItem(WL_KEY) || '[]'); } catch (e) { return []; }
  };
  const writeWishlist = (list) => {
    try { window.localStorage.setItem(WL_KEY, JSON.stringify(list)); } catch (e) { /* storage unavailable */ }
  };
  function paintWishlist(root) {
    if (window.BeyzaWishlist && typeof window.BeyzaWishlist.paint === 'function') {
      window.BeyzaWishlist.paint(root || document);
      return;
    }
    const list = readWishlist();
    (root || document).querySelectorAll('[data-beyza-wishlist]').forEach((btn) => {
      const on = list.indexOf(btn.dataset.productHandle) > -1;
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
  }
  function toggleWishlist(btn) {
    const handle = btn.dataset.productHandle;
    const productId = btn.dataset.productId;
    const nextActive = btn.getAttribute('aria-pressed') !== 'true';
    /* A wishlist app can take over by defining window.BeyzaWishlist.toggle(detail). */
    const hook = window.BeyzaWishlist && typeof window.BeyzaWishlist.toggle === 'function' ? window.BeyzaWishlist.toggle : null;
    const detail = { handle, productId, active: nextActive, button: btn };
    if (hook) {
      try { hook(detail); } catch (e) { /* app error should not break the card */ }
    } else {
      const list = readWishlist().filter((h) => h !== handle);
      if (nextActive) list.push(handle);
      writeWishlist(list);
    }
    document.querySelectorAll(`[data-beyza-wishlist][data-product-handle="${CSS.escape(handle)}"]`).forEach((b) => b.setAttribute('aria-pressed', nextActive ? 'true' : 'false'));
    announce(nextActive ? S.wishlistAdded : S.wishlistRemoved);
    document.dispatchEvent(new CustomEvent('beyza:wishlist:change', { detail }));
  }

  /* ---------------- Quick View (single shared dialog) ---------------- */
  const productCache = new Map();
  let lastTrigger = null;

  function productJsonUrl(card) {
    const base = (card.dataset.productUrl || '').split('?')[0].split('#')[0];
    return base ? `${base}.js` : '';
  }

  async function fetchProduct(url) {
    if (productCache.has(url)) return productCache.get(url);
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('Product request failed');
    const json = await res.json();
    productCache.set(url, json);
    return json;
  }

  function sized(src, width) {
    if (!src) return '';
    const url = src.startsWith('//') ? `https:${src}` : src;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + `width=${width}`;
  }

  function optionNames(product) {
    return (product.options || []).map((o) => (typeof o === 'string' ? o : o && o.name) || '');
  }

  function isDefaultOnly(product) {
    return product.variants.length === 1 && (product.variants[0].title === 'Default Title' || optionNames(product)[0] === 'Title');
  }

  function variantImage(product, variant) {
    if (variant && variant.featured_image && variant.featured_image.src) return variant.featured_image.src;
    return product.featured_image || (product.images && product.images[0]) || '';
  }

  function openDialog(trigger) {
    if (!DIALOG || typeof DIALOG.showModal !== 'function') return false;
    lastTrigger = trigger || null;
    if (!DIALOG.open) {
      DIALOG.showModal();
      document.documentElement.classList.add('beyza-qv-open');
    }
    return true;
  }

  function closeDialog() {
    if (!DIALOG || !DIALOG.open) return;
    DIALOG.close();
  }

  if (DIALOG) {
    DIALOG.addEventListener('close', () => {
      document.documentElement.classList.remove('beyza-qv-open');
      const body = DIALOG.querySelector('[data-bqv-body]');
      if (body) body.textContent = '';
      if (lastTrigger && lastTrigger.isConnected) {
        try { lastTrigger.focus({ preventScroll: true }); } catch (e) { lastTrigger.focus(); }
      }
      lastTrigger = null;
    });
    DIALOG.addEventListener('click', (e) => {
      if (e.target === DIALOG) closeDialog(); /* backdrop click */
      if (e.target instanceof Element && e.target.closest('[data-bqv-close]')) closeDialog();
    });
  }

  async function openQuickView(card, trigger) {
    const url = productJsonUrl(card);
    const productUrl = card.dataset.productUrl || '';
    if (!url || !openDialog(trigger)) {
      if (productUrl) window.location.href = productUrl;
      return;
    }
    const body = DIALOG.querySelector('[data-bqv-body]');
    body.textContent = '';
    const loading = document.createElement('div');
    loading.className = 'beyza-quick-view__loading';
    loading.setAttribute('role', 'status');
    loading.innerHTML = '<span class="beyza-quick-view__spinner" aria-hidden="true"></span>';
    const loadingText = document.createElement('span');
    loadingText.className = 'beyza-product-card__vh';
    loadingText.textContent = S.loading;
    loading.appendChild(loadingText);
    body.appendChild(loading);
    const closeBtn = DIALOG.querySelector('[data-bqv-close]');
    if (closeBtn) closeBtn.focus();

    try {
      const product = await fetchProduct(url);
      if (!DIALOG.open) return;
      renderQuickView(product, card);
    } catch (e) {
      closeDialog();
      if (productUrl) window.location.href = productUrl;
    }
  }

  function renderQuickView(product, card) {
    const body = DIALOG.querySelector('[data-bqv-body]');
    const names = optionNames(product);
    const defaultOnly = isDefaultOnly(product);
    const startId = Number(card.dataset.variantId);
    let variant = product.variants.find((v) => v.id === startId && v.available)
      || product.variants.find((v) => v.id === startId)
      || product.variants.find((v) => v.available)
      || product.variants[0];
    const selected = variant ? variant.options.slice() : names.map(() => '');
    const colorIndex = names.findIndex((n) => /colou?r|shade/i.test(n));

    /* Colour hints from the card's swatches (swatch colour / variant image). */
    const swatchFills = {};
    card.querySelectorAll('[data-beyza-swatch]').forEach((sw) => {
      const fill = sw.querySelector('.beyza-product-card__swatch-fill');
      if (fill) swatchFills[sw.getAttribute('title') || ''] = fill.getAttribute('style') || '';
    });

    body.textContent = '';
    const grid = document.createElement('div');
    grid.className = 'beyza-quick-view__grid';

    /* Gallery */
    const gallery = document.createElement('div');
    gallery.className = 'beyza-quick-view__gallery';
    const main = document.createElement('img');
    main.className = 'beyza-quick-view__main';
    main.alt = product.title;
    main.width = 900;
    main.height = 1200;
    gallery.appendChild(main);
    const images = (product.images || []).slice(0, 8);
    let thumbs = null;
    if (images.length > 1) {
      thumbs = document.createElement('div');
      thumbs.className = 'beyza-quick-view__thumbs';
      images.forEach((src, i) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'beyza-quick-view__thumb';
        b.dataset.src = src;
        b.setAttribute('aria-label', `${S.image} ${i + 1} / ${images.length}`);
        b.addEventListener('click', () => showImage(src));
        thumbs.appendChild(b);
      });
      gallery.appendChild(thumbs);
    }
    function showImage(src) {
      if (!src) return;
      main.src = sized(src, 900);
      main.srcset = `${sized(src, 600)} 600w, ${sized(src, 900)} 900w, ${sized(src, 1200)} 1200w`;
      main.sizes = '(min-width: 750px) 480px, 100vw';
      if (thumbs) {
        const clean = (u) => u.split('?')[0];
        thumbs.querySelectorAll('button').forEach((b) => b.setAttribute('aria-current', clean(b.dataset.src) === clean(src) ? 'true' : 'false'));
      }
    }

    /* Info */
    const info = document.createElement('div');
    info.className = 'beyza-quick-view__info';
    const title = document.createElement('h2');
    title.className = 'beyza-quick-view__title';
    title.id = 'BeyzaQuickViewTitle';
    title.textContent = product.title;
    const price = document.createElement('div');
    price.className = 'beyza-quick-view__price';
    info.append(title, price);

    /* Options */
    const fieldsets = [];
    if (!defaultOnly) {
      names.forEach((name, idx) => {
        const values = [];
        product.variants.forEach((v) => { if (values.indexOf(v.options[idx]) < 0) values.push(v.options[idx]); });
        const fs = document.createElement('fieldset');
        fs.className = 'beyza-quick-view__option';
        const legend = document.createElement('legend');
        legend.className = 'beyza-quick-view__legend';
        const legendValue = document.createElement('strong');
        legend.append(document.createTextNode(`${name}: `), legendValue);
        fs.appendChild(legend);
        const wrap = document.createElement('div');
        wrap.className = 'beyza-quick-view__values';
        const groupName = `bqv-option-${idx}`;
        values.forEach((value) => {
          const label = document.createElement('label');
          label.className = 'beyza-quick-view__value';
          const input = document.createElement('input');
          input.type = 'radio';
          input.name = groupName;
          input.value = value;
          input.checked = selected[idx] === value;
          input.addEventListener('change', () => {
            selected[idx] = value;
            update(true);
          });
          const span = document.createElement('span');
          if (idx === colorIndex) {
            const dot = document.createElement('i');
            dot.className = 'beyza-quick-view__dot';
            dot.setAttribute('aria-hidden', 'true');
            const style = swatchFills[value];
            if (style) dot.setAttribute('style', style);
            else dot.style.backgroundColor = value.toLowerCase().replace(/[\s-]/g, '');
            span.appendChild(dot);
          }
          span.appendChild(document.createTextNode(value));
          label.append(input, span);
          wrap.appendChild(label);
        });
        fs.appendChild(wrap);
        info.appendChild(fs);
        fieldsets.push({ fs, legendValue, idx });
      });
    }

    /* Quantity + add */
    const buy = document.createElement('div');
    buy.className = 'beyza-quick-view__buy';
    const qtyWrap = document.createElement('div');
    qtyWrap.className = 'beyza-quick-view__qty';
    const minus = document.createElement('button');
    minus.type = 'button';
    minus.textContent = '−';
    minus.setAttribute('aria-label', S.decrease);
    const qty = document.createElement('input');
    qty.type = 'number';
    qty.min = '1';
    qty.value = '1';
    qty.inputMode = 'numeric';
    qty.setAttribute('aria-label', S.quantity);
    const plus = document.createElement('button');
    plus.type = 'button';
    plus.textContent = '+';
    plus.setAttribute('aria-label', S.increase);
    qtyWrap.append(minus, qty, plus);
    const add = document.createElement('button');
    add.type = 'button';
    add.className = 'beyza-quick-view__add';
    const addLabel = document.createElement('span');
    addLabel.setAttribute('data-beyza-add-label', '');
    add.appendChild(addLabel);
    buy.append(qtyWrap, add);
    const error = document.createElement('p');
    error.className = 'beyza-quick-view__error';
    error.setAttribute('role', 'alert');
    const full = document.createElement('a');
    full.className = 'beyza-quick-view__link';
    full.textContent = S.viewFull;
    info.append(buy, error, full);

    const clampQty = () => {
      let n = parseInt(qty.value, 10);
      if (!Number.isFinite(n) || n < 1) n = 1;
      if (n > 99) n = 99;
      qty.value = String(n);
      minus.disabled = n <= 1;
      return n;
    };
    minus.addEventListener('click', () => { qty.value = String(clampQty() - 1); clampQty(); });
    plus.addEventListener('click', () => { qty.value = String(clampQty() + 1); clampQty(); });
    qty.addEventListener('change', clampQty);
    clampQty();

    add.addEventListener('click', () => {
      if (!variant || !variant.available) return;
      error.textContent = '';
      const drawerAutoOpens = !!document.querySelector('cart-drawer-component[auto-open]');
      runAdd(add, () => addToCart(variant.id, clampQty(), card, {
        productId: String(product.id),
        beforeDispatch: () => { if (drawerAutoOpens) closeDialog(); },
      })).catch((e) => { error.textContent = e && e.message ? e.message : S.error; });
    });

    function matchVariant() {
      return product.variants.find((v) => v.options.every((o, i) => o === selected[i])) || null;
    }

    function update(fromUser) {
      variant = defaultOnly ? product.variants[0] : matchVariant();
      /* price */
      const shown = variant || product.variants[0];
      const onSale = shown.compare_at_price && shown.compare_at_price > shown.price;
      const pct = onSale ? Math.round(((shown.compare_at_price - shown.price) * 100) / shown.compare_at_price) : 0;
      buildPrice(price, formatMoney(shown.price), onSale ? formatMoney(shown.compare_at_price) : '', pct, 'qv');
      /* option availability, given the other selected values */
      fieldsets.forEach(({ fs, legendValue, idx }) => {
        legendValue.textContent = selected[idx] || '';
        fs.querySelectorAll('.beyza-quick-view__value').forEach((label) => {
          const input = label.querySelector('input');
          const possible = product.variants.some((v) => v.available && v.options[idx] === input.value
            && v.options.every((o, i) => i === idx || o === selected[i]));
          label.classList.toggle('is-unavailable', !possible);
        });
      });
      /* button */
      add.classList.remove('is-added');
      if (!variant) {
        add.disabled = true;
        addLabel.textContent = S.unavailable;
      } else if (!variant.available) {
        add.disabled = true;
        addLabel.textContent = S.soldOut;
      } else {
        add.disabled = false;
        addLabel.textContent = S.add;
      }
      /* image + link */
      if (variant && (fromUser || !main.src)) showImage(variantImage(product, variant));
      if (!main.src) showImage(variantImage(product, shown));
      const base = (card.dataset.productUrl || product.url || '').split('?')[0];
      full.href = variant ? `${base}?variant=${variant.id}` : base;
    }

    grid.append(gallery, info);
    body.appendChild(grid);
    update(false);
    title.setAttribute('tabindex', '-1');
    title.focus({ preventScroll: true });
  }

  /* ---------------- Delegated events ---------------- */
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const wish = target.closest('[data-beyza-wishlist]');
    if (wish) {
      event.preventDefault();
      event.stopPropagation();
      toggleWishlist(wish);
      return;
    }

    const swatch = target.closest('[data-beyza-swatch]');
    if (swatch) {
      const card = cardOf(swatch);
      if (card) { event.preventDefault(); selectSwatch(card, swatch); }
      return;
    }

    const quick = target.closest('[data-beyza-quick-view]');
    if (quick) {
      const card = cardOf(quick);
      if (card) { event.preventDefault(); openQuickView(card, quick); }
      return;
    }

    const addBtn = target.closest('[data-beyza-add]');
    if (addBtn) {
      const card = cardOf(addBtn);
      if (!card || addBtn.disabled) return;
      event.preventDefault();
      if (card.dataset.addMode === 'quick') {
        openQuickView(card, addBtn);
        return;
      }
      runAdd(addBtn, () => addToCart(card.dataset.variantId, 1, addBtn, { productId: card.dataset.productId })).catch(() => {});
    }
  });

  /* Shared helpers for other Beyza components (e.g. the wishlist page). */
  window.BeyzaCart = window.BeyzaCart || {
    add: (variantId, quantity, sourceEl, opts) => addToCart(variantId, quantity || 1, sourceEl, opts || {}),
    formatMoney,
  };

  /* Mobile image slider dots (scroll events don't bubble, so listen in capture phase). */
  let dotFrame = 0;
  document.addEventListener('scroll', (event) => {
    const track = event.target;
    if (!(track instanceof Element) || !track.matches('[data-beyza-track]')) return;
    if (dotFrame) return;
    dotFrame = window.requestAnimationFrame(() => {
      dotFrame = 0;
      const card = cardOf(track);
      if (!card || !track.clientWidth) return;
      const index = Math.round(track.scrollLeft / track.clientWidth);
      card.querySelectorAll('.beyza-product-card__dot').forEach((d, i) => d.classList.toggle('is-active', i === index));
    });
  }, { capture: true, passive: true });

  /* Wishlist state for cards present now and cards added later (infinite scroll, filters, recommendations). */
  paintWishlist(document);
  let paintQueued = false;
  const observer = new MutationObserver((records) => {
    if (paintQueued) return;
    for (const r of records) {
      for (const n of r.addedNodes) {
        if (n instanceof Element && (n.matches('[data-beyza-card]') || n.querySelector('[data-beyza-card]'))) {
          paintQueued = true;
          window.requestAnimationFrame(() => { paintQueued = false; paintWishlist(document); });
          return;
        }
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
  window.addEventListener('storage', (e) => { if (e.key === WL_KEY) paintWishlist(document); });

}
